// app/javascript/dashboard/routes/dashboard/settings/emailTemplates/builder/useEmailDocument.js
// Estado do editor de e-mail v2: documento, seleção, desfazer/refazer
// (fotos JSON, agrupando digitação), copiar/colar entre e-mails
// (localStorage) e salvamento automático. Resposta do servidor só substitui
// o documento se nada mudou enquanto salvava (senão salvaria de novo).
import { ref, computed, getCurrentScope, onScopeDispose } from 'vue';
import API from 'dashboard/api/marketingEmailTemplates';
import SavedBlocksAPI from 'dashboard/api/emailSavedBlocks';
import * as ops from './document/ops';

export const SAVE_DELAY = 1500;
export const HISTORY_LIMIT = 100;
export const CLIPBOARD_KEY = 'cs-email-clipboard';
export const HIGHLIGHT_MS = 2000;
const GROUP_WINDOW = 1000;
const RETRY_DELAYS = [2000, 4000, 8000, 16000, 30000];

export function useEmailDocument({
  templateId,
  onError,
  onLimit,
  api = API,
  blocksApi = SavedBlocksAPI,
  storage = window.localStorage,
} = {}) {
  const template = ref(null);
  const catalog = ref(null);
  const doc = ref(null);
  const loading = ref(true);
  const selectedId = ref(null);
  const device = ref('desktop');
  const saveState = ref('saved');
  const converted = ref(false);
  const undoStack = ref([]);
  const redoStack = ref([]);
  // Sobe a cada mudança do histórico: o chat de IA usa para saber se o
  // "Desfazer" ainda é da resposta dele.
  const historyVersion = ref(0);
  // Montagem paga pela IA em andamento: a tela trava a edição para a resposta
  // (que já foi cobrada) não ser descartada por mudança no meio da espera.
  const composing = ref(false);
  const highlightIds = ref([]);
  let highlightTimer = null;
  let version = 0;
  let lastGroup = null;
  let lastGroupAt = 0;
  let saveTimer = null;
  let retry = 0;
  let inFlight = null;
  let again = false;
  let disposed = false;
  let allowFinal = false;

  const selected = computed(() => ops.findNode(doc.value, selectedId.value));
  const canUndo = computed(() => undoStack.value.length > 0);
  const canRedo = computed(() => redoStack.value.length > 0);

  const snapshot = () => JSON.stringify(doc.value);

  const scheduleSave = (delay = SAVE_DELAY) => {
    clearTimeout(saveTimer);
    if (disposed) return;
    saveTimer = setTimeout(() => save(), delay); // eslint-disable-line no-use-before-define
  };

  const markDirty = () => {
    version += 1;
    saveState.value = 'dirty';
    scheduleSave();
  };

  const commit = (next, { group } = {}) => {
    if (!next || next === doc.value) return;
    const now = Date.now();
    const sameGroup =
      group && group === lastGroup && now - lastGroupAt < GROUP_WINDOW;
    if (!sameGroup) {
      undoStack.value.push(snapshot());
      if (undoStack.value.length > HISTORY_LIMIT) undoStack.value.shift();
    }
    lastGroup = group || null;
    lastGroupAt = now;
    redoStack.value = [];
    historyVersion.value += 1;
    doc.value = next;
    markDirty();
  };

  const undo = () => {
    if (!undoStack.value.length) return;
    redoStack.value.push(snapshot());
    doc.value = JSON.parse(undoStack.value.pop());
    lastGroup = null;
    historyVersion.value += 1;
    markDirty();
  };
  const redo = () => {
    if (!redoStack.value.length) return;
    undoStack.value.push(snapshot());
    doc.value = JSON.parse(redoStack.value.pop());
    lastGroup = null;
    historyVersion.value += 1;
    markDirty();
  };

  async function performSave() {
    const sent = version;
    saveState.value = 'saving';
    try {
      const { data } = await api.update(template.value.id, {
        marketing_email_template: {
          name: template.value.name,
          subject: template.value.subject,
          mode: template.value.mode,
          settings: template.value.settings || {},
          layout: doc.value,
        },
      });
      retry = 0;
      converted.value = false;
      if (version === sent) {
        template.value = { ...template.value, ...data, layout: undefined };
        doc.value = data.layout || doc.value;
        saveState.value = 'saved';
      } else {
        // Houve edição durante o salvamento: preserva os campos locais.
        const { name, subject, mode, settings } = template.value;
        template.value = {
          ...template.value,
          ...data,
          name,
          subject,
          mode,
          settings,
          layout: undefined,
        };
        saveState.value = 'dirty';
        scheduleSave();
      }
      return true;
    } catch (error) {
      saveState.value = 'error';
      onError?.(error);
      scheduleSave(RETRY_DELAYS[Math.min(retry, RETRY_DELAYS.length - 1)]);
      retry += 1;
      return false;
    }
  }

  // Um PUT por vez: pedido durante um salvamento roda depois dele.
  function save() {
    if ((disposed && !allowFinal) || !template.value || !doc.value)
      return Promise.resolve(false);
    clearTimeout(saveTimer);
    if (inFlight) {
      again = true;
      return inFlight;
    }
    inFlight = (async () => {
      let ok;
      do {
        again = false;
        ok = await performSave(); // eslint-disable-line no-await-in-loop
      } while (again && (!disposed || allowFinal));
      inFlight = null;
      return ok;
    })();
    return inFlight;
  }

  const flush = async () => {
    clearTimeout(saveTimer);
    if (inFlight) await inFlight;
    if (['dirty', 'error'].includes(saveState.value)) await save();
  };

  // Para timers e retries, mas tenta UMA última vez salvar o que estiver
  // pendente (inclusive depois de um salvamento em andamento).
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    clearTimeout(saveTimer);
    clearTimeout(highlightTimer);
    highlightIds.value = [];
    allowFinal = true;
    (inFlight || Promise.resolve())
      .then(async () => {
        if (['dirty', 'error'].includes(saveState.value)) await save();
      })
      .finally(() => {
        allowFinal = false;
      });
  };
  if (getCurrentScope()) onScopeDispose(dispose);

  const load = async () => {
    loading.value = true;
    try {
      const [{ data: tpl }, { data: cat }] = await Promise.all([
        api.show(templateId),
        api.catalog(),
      ]);
      catalog.value = cat;
      doc.value = tpl.layout;
      converted.value = !!tpl.layout_converted;
      template.value = { ...tpl, layout: undefined };
    } catch (error) {
      onError?.(error);
    } finally {
      loading.value = false;
    }
  };

  // ---- seleção ----
  const select = id => {
    selectedId.value = id || null;
  };
  const selectParent = () => {
    const s = selected.value;
    if (!s) return;
    selectedId.value = s.kind === 'row' ? null : s.row.id;
  };

  // ---- edições ----
  const rowIndexForInsert = () =>
    selected.value ? selected.value.rowIndex + 1 : undefined;

  const limits = () => catalog.value?.layout?.limits;
  // Operação que passaria do limite do servidor: avisa e não muda nada.
  const attempt = fn => {
    try {
      return fn();
    } catch (error) {
      if (!(error instanceof ops.LimitError)) throw error;
      onLimit?.(error.kind);
      return null;
    }
  };

  const addStructure = (widths, index) => {
    const row = ops.buildRow(catalog.value, widths);
    const next = attempt(() =>
      ops.insertRows(doc.value, [row], index ?? rowIndexForInsert(), limits())
    );
    if (!next) return null;
    commit(next);
    selectedId.value = row.id;
    return row;
  };

  const addSection = (section, index) => {
    const rows = ops.buildSectionRows(section);
    const next = attempt(() =>
      ops.insertRows(doc.value, rows, index ?? rowIndexForInsert(), limits())
    );
    if (!next) return;
    commit(next);
    selectedId.value = rows[0]?.id || null;
  };

  const addElement = (type, { columnId, index } = {}) => {
    const el = ops.buildElement(catalog.value, type);
    let target = columnId;
    let at = index;
    const s = selected.value;
    if (!target && s?.kind === 'element') {
      target = s.column.id;
      at = s.elIndex + 1;
    }
    if (!target && s?.kind === 'column') target = s.column.id;
    if (!target && s?.kind === 'row') target = s.row.columns[0].id;
    const next = attempt(() => {
      if (target) {
        return ops.insertElement(doc.value, target, el, at, limits());
      }
      const row = ops.buildRow(catalog.value, [100], [[el]]);
      return ops.insertRows(doc.value, [row], rowIndexForInsert(), limits());
    });
    if (!next) return null;
    commit(next);
    selectedId.value = el.id;
    return el;
  };

  const moveElement = (id, columnId, index) =>
    commit(
      attempt(() => ops.moveElement(doc.value, id, columnId, index, limits()))
    );
  const moveRow = (id, index) => commit(ops.moveRow(doc.value, id, index));
  const remove = id => {
    const next = ops.removeNode(doc.value, id);
    if (next === doc.value) return false;
    commit(next);
    if (selectedId.value === id) selectedId.value = null;
    return true;
  };
  const duplicate = id => {
    const out = attempt(() => ops.duplicateNode(doc.value, id, limits()));
    if (!out) return;
    commit(out.doc);
    selectedId.value = out.id;
  };
  const update = (id, patch, opts) =>
    commit(ops.updateNode(doc.value, id, patch), opts);
  const updateSettings = patch =>
    commit(
      {
        ...JSON.parse(snapshot()),
        settings: { ...(doc.value.settings || {}), ...patch },
      },
      { group: `settings:${Object.keys(patch).join(',')}` }
    );
  const setWidths = (rowId, widths) =>
    commit(ops.setColumnWidths(doc.value, rowId, widths), {
      group: `widths:${rowId}`,
    });
  const setStructure = (rowId, widths) =>
    commit(attempt(() => ops.setStructure(doc.value, rowId, widths, limits())));
  const updateTemplate = patch => {
    template.value = { ...template.value, ...patch };
    markDirty();
  };

  const copy = id => {
    const found = ops.findNode(doc.value, id);
    if (!found || found.kind === 'column') return;
    const clip = { kind: found.kind, node: found[found.kind] };
    try {
      storage.setItem(CLIPBOARD_KEY, JSON.stringify(clip));
    } catch (e) {
      /* armazenamento indisponível */
    }
  };
  const paste = () => {
    let clip = null;
    try {
      clip = JSON.parse(storage.getItem(CLIPBOARD_KEY) || 'null');
    } catch (e) {
      clip = null;
    }
    if (!clip || !['row', 'element'].includes(clip.kind)) return;
    const out = attempt(() =>
      ops.pasteNode(doc.value, clip, selectedId.value, limits())
    );
    if (!out?.id) return;
    commit(out.doc);
    selectedId.value = out.id;
  };

  // ---- blocos salvos ("Meus blocos") ----
  const savedBlocks = ref([]);
  const savedLoaded = ref(false);
  const saveBlockTarget = ref(null);

  const loadSavedBlocks = async () => {
    try {
      const { data } = await blocksApi.get();
      savedBlocks.value = data;
      savedLoaded.value = true;
    } catch (error) {
      onError?.(error);
    }
  };
  // Linha ou elemento fora do rodapé (coluna e rodapé nunca viram bloco).
  const canSaveAsBlock = id => {
    const found = ops.findNode(doc.value, id);
    return !!found && found.kind !== 'column' && !ops.isFooterRow(found.row);
  };
  const requestSaveBlock = id => {
    if (canSaveAsBlock(id)) saveBlockTarget.value = id;
  };
  // Erro do servidor sobe para o diálogo mostrar a mensagem.
  const saveAsBlock = async (id, name) => {
    if (!canSaveAsBlock(id)) return null;
    const found = ops.findNode(doc.value, id);
    const { data } = await blocksApi.create({
      name,
      kind: found.kind,
      node: found[found.kind],
    });
    savedBlocks.value = [
      data,
      ...savedBlocks.value.filter(b => b.id !== data.id),
    ];
    return data;
  };
  const insertSaved = (saved, target = {}) => {
    const out = attempt(() =>
      ops.insertSavedNode(
        doc.value,
        catalog.value,
        saved,
        { selectedId: selectedId.value, ...target },
        limits()
      )
    );
    if (!out?.id || out.doc === doc.value) return;
    commit(out.doc);
    selectedId.value = out.id;
  };
  const renameSaved = async (id, name) => {
    const { data } = await blocksApi.update(id, { name });
    savedBlocks.value = savedBlocks.value.map(b => (b.id === id ? data : b));
  };
  const deleteSaved = async id => {
    await blocksApi.delete(id);
    savedBlocks.value = savedBlocks.value.filter(b => b.id !== id);
  };

  // Resultado da IA (compor): troca o e-mail inteiro num passo de desfazer.
  const applyLayout = layout => {
    if (!layout?.rows) return;
    commit(JSON.parse(JSON.stringify(layout)));
    selectedId.value = null;
  };

  // Resposta do chat de IA: um passo de desfazer; a seleção só fica se o nó
  // ainda existe no e-mail novo.
  const applyAiLayout = layout => {
    if (!layout?.rows) return;
    commit(JSON.parse(JSON.stringify(layout)));
    if (!ops.findNode(doc.value, selectedId.value)) selectedId.value = null;
  };

  // O que a IA mudou fica destacado no canvas por HIGHLIGHT_MS.
  const flash = ids => {
    if (disposed) return;
    clearTimeout(highlightTimer);
    highlightIds.value = [...(ids || [])];
    highlightTimer = setTimeout(() => {
      highlightIds.value = [];
    }, HIGHLIGHT_MS);
  };

  return {
    template,
    catalog,
    doc,
    loading,
    selectedId,
    selected,
    device,
    saveState,
    converted,
    canUndo,
    canRedo,
    load,
    save,
    flush,
    dispose,
    undo,
    redo,
    select,
    selectParent,
    addElement,
    addStructure,
    addSection,
    moveElement,
    moveRow,
    remove,
    duplicate,
    update,
    updateSettings,
    setWidths,
    setStructure,
    updateTemplate,
    copy,
    paste,
    applyLayout,
    applyAiLayout,
    historyVersion,
    composing,
    highlightIds,
    flash,
    savedBlocks,
    savedLoaded,
    saveBlockTarget,
    loadSavedBlocks,
    canSaveAsBlock,
    requestSaveBlock,
    saveAsBlock,
    insertSaved,
    renameSaved,
    deleteSaved,
  };
}
